import { inject, Injectable } from "@angular/core";
import { OriginImportData } from "../../gloss.sync";
import { AccountView } from "../../../../models/view/account/account.view";
import { GlossSyncService } from "../../gloss.sync.service";
import { BasiqJobSync } from "./basiq.job.sync";
import { BasiqUserValidator } from "../validators/basiq.validator.user";
import { BasiqInstitutionSync } from "./basiq.institution.sync";
import { BasiqUserAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.user";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";

export type InstitutionAccountsMap = Map<string, AccountView[]>;
export interface GlossSyncInterface {
  import(originImportData: OriginImportData): Promise<void>;
  sync(accounts: AccountView[]): Promise<void>;
  connect(): Promise<void>; //Connect for the first time
  connectToInstitution(institution: InstitutionView): Promise<void>; // Connect to an institution that has been connected before
  reconnectToInstitution(institution: InstitutionView): Promise<void>; // Connect to an institution that has been connected before after a consent removal
}

@Injectable({
  providedIn: "root",
})
export class BasiqSync implements GlossSyncInterface {
  constructor(
    private basiqInstitutionSync: BasiqInstitutionSync = inject(BasiqInstitutionSync),
    private glossSyncService: GlossSyncService = inject(GlossSyncService),
    private basiqJobSync: BasiqJobSync = inject(BasiqJobSync),
    private basiqUserValidator: BasiqUserValidator = inject(BasiqUserValidator),
    private basiqUserAction: BasiqUserAction = inject(BasiqUserAction),
  ) {}

  async connect(isFromGuide: boolean = false): Promise<void> {
    try {
      if (!(await this.basiqUserValidator.isBasiqUser())) {
        await this.basiqUserAction.connectNewUser();
        return;
      }
      const isUserConsent = await this.basiqUserValidator.isUserConsent();
      await this.basiqUserAction.connectExistingUser(isUserConsent);
    } catch (error) {
      await this.glossSyncService.logError([], error, "connecting to basiq", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  /** Redirects user ta the specific institution in Basiq */
  async connectToInstitution(institution: InstitutionView): Promise<void> {
    try {
      await this.basiqUserAction.connectToInstitution(institution);
    } catch (error) {
      await this.glossSyncService.logError([], error, "connecting to basiq", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  /** Redirects user to consent screen first and then to the specified insto's login page */
  async reconnectToInstitution(institution: InstitutionView): Promise<void> {
    try {
      await this.basiqUserAction.reconnectToInstitution(institution);
    } catch (error) {
      await this.glossSyncService.logError([], error, "connecting to basiq", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  async import(originImportData: OriginImportData): Promise<void> {
    if (typeof originImportData !== "string") {
      return;
    }
    /** Since we have the jobIds, from this point on we can go on with job level import */
    await this.basiqJobSync.import(originImportData);
  }

  async sync(accounts: AccountView[]) {
    if (!(await this.basiqUserValidator.isValid())) {
      await this.glossSyncService.terminateSync(accounts, this.basiqUserValidator.syncStatus);
      return;
    }
    await this.runInstoLevelSync(accounts);
  }

  private async runInstoLevelSync(accounts: AccountView[]) {
    const instoAccountMap = this.instoAccountMap(accounts);
    for (const [instoBasiqId, accounts] of instoAccountMap) {
      await this.basiqInstitutionSync.run(instoBasiqId, accounts);
    }
  }

  private instoAccountMap(accounts: AccountView[]): InstitutionAccountsMap {
    const instoAccountMap: InstitutionAccountsMap = new Map();
    for (const account of accounts) {
      const instoBasiqId = account.c_institution.instoBasiqId;
      if (!instoAccountMap.has(instoBasiqId)) {
        instoAccountMap.set(instoBasiqId, []);
      }
      instoAccountMap.get(instoBasiqId).push(account);
    }
    return instoAccountMap;
  }
}
